import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import { useAuth } from "../context/AuthContext";
import { useActiveRole } from "../context/ActiveRoleContext";
import { ROLE_NAMES, USER_ROLES, MAPS_ROLE_DESCRIPTIONS } from "../constants/userRoles";

import logo from "../assets/icon.png";

/**
 * "¿Con qué rol quieres entrar?" — mismo flujo que el CMS (pages/auth/RoleSelector.jsx), pero con
 * el estilo de maps (CSS embebido, como Login.jsx).
 *
 * `availableRoles` ya viene FILTRADO a los roles con acceso a maps (lo filtra AuthContext al
 * loguear), asi que aca no se vuelve a decidir quien entra.
 */
const RoleSelector = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { availableRoles, setActiveRole } = useActiveRole();

  const fullName =
    `${user?.name || ""} ${user?.lastName || ""}`.trim() || user?.userEmail || "Usuario";

  const handleRoleSelection = (roleKey) => {
    const roleId = USER_ROLES[roleKey];
    if (roleId === undefined) {
      console.error(`Rol invalido: ${roleKey}`);
      return;
    }
    setActiveRole(roleId);
    navigate("/tracking", { replace: true });
  };

  // Con un solo rol no hay nada que preguntar: se entra directo. Pasa cuando el usuario llega aca
  // por "Cambiar rol" teniendo uno solo, o al recargar la pagina.
  // Sin roles permitidos no deberia haber llegado (el login lo corta), pero si pasa se cierra
  // sesion en vez de dejarlo en una pantalla vacia.
  useEffect(() => {
    if (!availableRoles || availableRoles.length === 0) {
      logout();
      navigate("/login", { replace: true });
      return;
    }
    if (availableRoles.length === 1) {
      handleRoleSelection(availableRoles[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableRoles]);

  if (!availableRoles || availableRoles.length <= 1) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="role-select-layout"
    >
      <style>{`
        .role-select-layout * { box-sizing: border-box; }

        .role-select-layout {
          min-height: 100vh;
          width: 100vw;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 24px;
          background: linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%);
          font-family: 'Poppins', sans-serif;
        }

        .role-select-logo { width: 120px; height: auto; margin-bottom: 32px; }

        .role-select-header { text-align: center; margin-bottom: 40px; max-width: 620px; }
        .role-select-header h1 {
          font-size: 34px;
          font-weight: 800;
          color: #4B4A4A;
          letter-spacing: -0.5px;
          margin-bottom: 12px;
        }
        .role-select-header p { font-size: 15px; color: #6B7280; line-height: 1.6; }
        .role-select-header strong { color: #4B4A4A; font-weight: 700; }

        .role-card-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 20px;
          width: 100%;
          max-width: 880px;
        }

        .role-card {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 10px;
          padding: 28px 24px;
          background: #FFFFFF;
          border: 2px solid #E5E7EB;
          border-radius: 16px;
          cursor: pointer;
          text-align: left;
          font-family: inherit;
          transition: all 0.2s ease;
        }

        .role-card:hover {
          border-color: #EAC024;
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(234, 192, 36, 0.18);
        }

        .role-card:focus-visible {
          outline: none;
          border-color: #EAC024;
          box-shadow: 0 0 0 4px rgba(234, 192, 36, 0.25);
        }

        .role-card-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: #FDF6DC;
          color: #B9900F;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 6px;
        }

        .role-card-name { font-size: 19px; font-weight: 700; color: #111827; }
        .role-card-desc { font-size: 13px; color: #6B7280; line-height: 1.5; }

        .role-select-footer {
          margin-top: 36px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }
        .role-select-logout {
          background: none;
          border: none;
          font-family: inherit;
          font-size: 13px;
          font-weight: 600;
          color: #9CA3AF;
          cursor: pointer;
          text-decoration: underline;
        }
        .role-select-logout:hover { color: #6B7280; }
      `}</style>

      <img src={logo} alt="En La Parada" className="role-select-logo" />

      <div className="role-select-header">
        <h1>Selecciona tu rol</h1>
        <p>
          Hola <strong>{fullName}</strong>, tienes acceso a maps con más de un rol. Elige con cuál
          quieres entrar — determina qué líneas vas a ver.
        </p>
      </div>

      <div className="role-card-grid">
        {availableRoles.map((roleKey) => (
          <button
            key={roleKey}
            type="button"
            className="role-card"
            onClick={() => handleRoleSelection(roleKey)}
          >
            <span className="role-card-icon" aria-hidden="true">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </span>
            <span className="role-card-name">
              {ROLE_NAMES[USER_ROLES[roleKey]] || roleKey}
            </span>
            <span className="role-card-desc">{MAPS_ROLE_DESCRIPTIONS[roleKey] || ""}</span>
          </button>
        ))}
      </div>

      <div className="role-select-footer">
        <button
          type="button"
          className="role-select-logout"
          onClick={() => {
            logout();
            navigate("/login", { replace: true });
          }}
        >
          Cerrar sesión
        </button>
      </div>
    </motion.div>
  );
};

export default RoleSelector;
